<script setup>
import {computed, onMounted, reactive, ref} from 'vue'
import DeviceScreenshot from '@common/DeviceScreenshot'
import Keyboard from '@common/Keyboard'
import {Inertia} from '@inertiajs/inertia'
import {useBreadStore} from '@bread/js/store'
import {DateTime} from 'luxon'
import LogViewer from "@common/LogViewer";
import moment from 'moment';
import {Link} from "@inertiajs/inertia-vue3";

const store = useBreadStore()
const selected = reactive({})
let channel = reactive({})
const screenshots = reactive({})
const logs = reactive({})
let keyboardInput = ref('')
let keyPress = ref('')
const emits = defineEmits(['filter'])

onMounted(() => {
  store.setResource('device')
})

const selectDevice = (device) => {
  if (selected.device?.id === device.id) {
    notify({event: 'left', data: selected.device})
    window.Echo.leave(`devices.${selected.device.external_identifier}`)
    selected.device = null
  } else {
    channel = window.Echo.private(`devices.${device.external_identifier}`)
    channel.listen('.client-screenshot', (data) => {
      console.log(data)
      screenshots[data.identifier] = data.filename + '?' + +new Date()
    })
    setTimeout(() => {
      selected.device = device
      channel.whisper('joined', {device: selected.device})
    }, 1000)
  }
}

const sendKeyboardInput = () => {
  notify({event: 'type', data: {string: keyboardInput.value}})
  keyboardInput.value = ''
}

const sendKeyboardPress = (data) => {
  notify({event: 'press', data})
  keyboardInput.value = ''
}

const notify = (data) => {
  console.log(data.event, data.data)
  channel.whisper(data.event, data.data)
}

const deselectDevice = () => {
  selected.device = null
}

const toggleRecorder = (device) => {
  device.is_recording = !device.is_recording;
  console.log(device.external_identifier)

  axios.post(
    route('api.devices.record.device', {
        id: device.external_identifier,
      },
      {headers: {'Identifier': device.external_identifier}}))
}

onMounted(() => {
  for (let device of Inertia.page.props.devices) {
    logs[device.external_identifier] = device.logs
    screenshots[device.external_identifier] = device.latest_screenshot
  }

let userMail = Inertia.page.props.user.email;
  if(userMail.split('@')[1]==='libaro.be'){
    showRecordings.value = true;
  }
})

const logOpen = ref(false)
let openLogFile = ref('')
let openLogContents = ref('')
let logDevice = ref({})

const showLog = (device, filename, contents) => {
  openLogFile.value = filename;
  openLogContents.value = contents;
  logOpen.value = true;
  logDevice.value = device;
}

const rebootPc = (device) => {
  axios.post(
    route('api.devices.reboot.pc', {
      id: device.external_identifier,
    }))
    .then((response) => {
      emits('PC Rebooted', response.data.data)
    })
    .catch((error) => {
      emits('error', {message: error.response.data.message})
    })
}

const rebootDevice = (device) => {
  axios.post(
    route('api.devices.reboot.device', {
      id: device.external_identifier,
    }))
    .then((response) => {
      emits('Device Rebooted', response.data.data)
    })
    .catch((error) => {
      emits('error', {message: error.response.data.message})
    })
}
const closeLog = () => {
  logOpen.value = false;
}

const showRecordings = ref(false);
</script>

<template>
  <div class="flex h-full w-full flex-col items-center justify-center">
    <div
      style="height: 75vh"
      class="flex w-full items-center justify-center bg-gray-700 py-4"
    >
      <div class="flex" v-if="selected.device">
        <DeviceScreenshot
          :device="selected.device"
          @clicked="notify"
          :src="screenshots[selected.device.external_identifier]"
          style="max-height: 70vh"
          class="max-h-full"
          :isSelectedDevice="true"
        >
        </DeviceScreenshot>
        <div>
          <Keyboard
            @pressed="sendKeyboardPress"
            :device="selected.device"
          ></Keyboard>
        </div>
      </div>
      <div
        v-else
        class="flex h-full w-full items-center justify-center text-white"
      >
        Kies een toestel hieronder...
      </div>
    </div>
    <div class="flex h-full flex-wrap flew-row mt-6 mb-6 justify-around w-full">
      <div class="card xl:w-96 w-80 bg-base-50 shadow-xl"
           :class="device.id === selected.device?.id ? 'bg-primary text-primary-content' : ''"
           v-for="device in $attrs.devices">
        <div class="card-body">
          <h2 class="card-title">
            {{ device.node_name }}
            <div class="badge badge-info"> {{ parseInt(device.temperature) }} °C</div>
          </h2>
          <p v-if="device.last_seen">
            Laatst Gezien op
            {{
              DateTime.fromISO(device.last_seen).toLocaleString({
                locale: 'nl',
                ...DateTime.DATETIME_MED,
              })
            }}
          </p>
          <div class="card-actions">
            <button v-if="device.id !== selected.device?.id" class="btn btn-success" @click="selectDevice(device)" title="Open stream">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z"/>
              </svg>
            </button>
            <button v-if="device.id === selected.device?.id" class="btn btn-error" @click="selectDevice(device)" title="Open stream">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5"/>
              </svg>
            </button>
            <button class="btn btn-danger gap-2" @click="rebootDevice(device)" title="Reboot device connected to pc">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
              </svg>
              Device
            </button>
            <button class="btn btn-danger gap-2" @click="rebootPc(device)" title="Reboot Onebox PC">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"/>
              </svg>
              PC
            </button>
            <input type="text" :value="device.token" class="input input-bordered w-full max-w-xs"/>
            <Link v-if="showRecordings" :href="route('admin.devices.recordings',device.id)" class="px-4 py-2 bg-secondary rounded-full text-center">Toon Opnames</Link>
          </div>
        </div>
        <figure>
          <label :for="'modalScreenLog-'+device.id" class="btn btn-info absolute right-5 bottom-50 mb-2 text-white btn-square">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
          </label>
          <input type="checkbox" :id="'modalScreenLog-'+device.id" class="modal-toggle" />
          <div class="modal modal-bottom sm:modal-middle">
            <div class="modal-box relative">
              <label :for="'modalScreenLog-'+device.id" class="btn btn-sm btn-circle absolute right-2 top-2">✕</label>
              <h3 class="font-bold text-lg">{{device.node_name}}</h3>
              <div>
                <div class="relative mb-2" v-for="image in device.media">
                  <img :src="image.original_url" loading="lazy" alt="">
                  <span class="badge badge-ghost absolute bottom-0 right-0">{{(moment(image.created_at)).format('DD/MM/YYYY HH:mm:ss')}}</span>
                </div>

              </div>
            </div>
          </div>
          <button class="btn btn-square absolute right-5 bottom-5 btn-error" @click="toggleRecorder(device)" title="Start saving stream">
            <svg v-if="device.is_recording" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                 stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
              <path stroke-linecap="round"
                    d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                 stroke="currentColor" class="w-6 h-6">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M12 18.75H4.5a2.25 2.25 0 01-2.25-2.25V9m12.841 9.091L16.5 19.5m-1.409-1.409c.407-.407.659-.97.659-1.591v-9a2.25 2.25 0 00-2.25-2.25h-9c-.621 0-1.184.252-1.591.659m12.182 12.182L2.909 5.909M1.5 4.5l1.409 1.409"/>
            </svg>
          </button>
          <DeviceScreenshot
            :device="device"
            :src="screenshots[device.external_identifier]"
            :isSelectedDevice="false"></DeviceScreenshot>
        </figure>
      </div>
    </div>
    <!--    <div class="w-full flex flex-wrap flex-row bg-white p-5 rounded-md" v-if="selected.device">-->
    <!--      <h2 class="cursor-pointer mb-5 flex items-center justify-between border-b border-gray-200 pb-3 font-bold w-full">Geschiedenis</h2>-->
    <!--      <div class="" v-for="(screenshot, timestamp) in selected.device.screenshots">-->
    <!--        <img class="p-2 w-64" :src="screenshot" alt="">-->
    <!--        <p>{{timestamp}}</p>-->
    <!--      </div>-->
    <!--    </div>-->
    <LogViewer v-if="logOpen" :filename="openLogFile" :device="logDevice" @close="closeLog()"></LogViewer>
  </div>
</template>
